/**
 * metricTiering — helpers for the DEMO per-value metric gate.
 * ==========================================================
 * For DEMO users the explorer response carries `cell._gated` (a Set of
 * cell-shape keys whose exact value the backend withheld) plus coarse teaser
 * bands on the quality metrics. These helpers let the Grid/Table/Detail
 * renderers decide, per premium value, whether to draw the exact number, a
 * teaser band chip, a PRO lock chip, or a genuine "—" — without each renderer
 * re-implementing the rules. See explorerCellShape.js for how `_gated`/bands are
 * built from the frozen backend contract.
 */
import { EXPLORER_KEY_TO_CELL_SHAPE_KEY } from '../modules/explorerCellShape.js'
import { isDemoCatalogUnlocked, isCellUnlockedToday } from '../state/demoLimitStore.js'

// Cell-shape key → upsell/telemetry category. Drives the category-framed modal
// copy and the /checkout lead_source. Anything unmapped falls back to 'metrics'.
const SHAPE_KEY_TO_CATEGORY = {
  'electrical.R_dc_mOhm': 'electrical',
  'electrical.R_EIS_mOhm': 'electrical',
  'electrical.R_ac_mOhm': 'electrical',
  'electrical.R_pol_25': 'electrical',
  'electrical.eta_dc_pct': 'electrical',
  'electrical.P_dc_25_W': 'electrical',
  'electrical.W_dc_25_Wh': 'electrical',
  'electrical.I_dc_25_A': 'electrical',
  'electrical.t_max_dc_25': 'electrical',
  'thermal.delta_T_dc_25': 'thermal',
  'thermal.T_max_dc_25': 'thermal',
  'thermal.P_loss_dc_25': 'thermal',
  'thermal.W_loss_dc_25': 'thermal',
  'batch.CAP_spread_std_rel': 'quality',
  'batch.EIS_spread_std_rel': 'quality',
  'aging.CAP_loss_rel': 'quality',
  'aging.EIS_increase_rel': 'quality',
}

/** True when the given cell-shape key is gated (exact value withheld) for this cell. */
export function isGated(cell, shapeKey) {
  const gated = cell?._gated
  return gated instanceof Set ? gated.has(shapeKey) : false
}

/** Upsell/telemetry category for a cell-shape key (falls back to 'metrics'). */
export function categoryForShapeKey(shapeKey) {
  return SHAPE_KEY_TO_CATEGORY[shapeKey] || 'metrics'
}

/**
 * Count of tiered premium metrics on a cell — the size of the gated set
 * (counting only the mapped cell-shape keys, not the raw backend-key duplicates
 * we also stash) plus any teaser bands present. Drives the aggregate
 * "+N more measured metrics with PRO" cue. Returns 0 when nothing is tiered
 * (PRO / admin / internal), so callers can hide the cue entirely.
 */
export function countTieredMetrics(cell) {
  if (!cell) return 0
  let count = 0
  const gated = cell._gated
  if (gated instanceof Set) {
    // `_gated` holds both the mapped shape keys AND the raw backend keys (best-
    // effort fallback). Count each gated metric once by matching the mapped
    // shape keys, which are the canonical entries.
    const shapeKeys = new Set(Object.values(EXPLORER_KEY_TO_CELL_SHAPE_KEY))
    for (const key of gated) {
      if (shapeKeys.has(key)) count += 1
    }
  }
  // Teaser bands are a separate tier signal (the exact pct is null, not gated).
  if (cell.batch?.CAP_spread_band) count += 1
  if (cell.aging?.CAP_loss_band) count += 1
  return count
}

/** True when this cell has any tiered metric (gated value or teaser band). */
export function hasTieredMetrics(cell) {
  return countTieredMetrics(cell) > 0
}

/**
 * True when this cell's chemistry value/badge must render as a demo-display
 * lock instead of the real value (Grid/Table governing rule — Decision 1A
 * soft friction). Unlike the `_gated`/band premium metrics above, chemistry is
 * NOT withheld by the backend — `main_chemistry` is always sent — so this is a
 * purely client-side display gate. It MUST NOT be consulted by the chemistry
 * filter/sort (those keep matching against the real value; only the rendered
 * badge/cell is affected).
 *
 * False for any non-demo tier (`isDemoCatalogUnlocked()` false), and false
 * again once the cell has been unlocked today — `isCellUnlockedToday` is the
 * single global source both the in-place unlock and a same-day detail view
 * update, so Grid/Table/Plot all agree on unlock state.
 *
 * Final-review fix (Task 11): also require the ROW ITSELF to actually carry
 * server-side gating (`cell._gated` non-empty). Internal (`@batemo.de`)
 * accounts on a DEMO subscription are exempt server-side — the backend sends
 * them exact values with an empty `gated_fields` (see explorerCellShape.js:
 * "Both null/empty for unlocked / exempt / PRO rows"). Gating purely off the
 * client tier meant those mixed accounts saw chemistry locked with no way to
 * unlock it (POST /demo/unlock 403s — demo-only). A genuine (non-exempt) demo
 * row always carries at least one gated field, so this doesn't affect real
 * demo users.
 */
export function isChemistryLockedForDemo(cell) {
  return (
    isDemoCatalogUnlocked() &&
    !isCellUnlockedToday(cell?.id) &&
    cell?.chemical?.main_chemistry != null &&
    cell?._gated instanceof Set &&
    cell._gated.size > 0
  )
}
